// Dynamically update Open Graph / Twitter Card meta tags so that shared
// ?appId= links render rich previews in Slack, iMessage, and social clients
// that execute JavaScript before scraping (issue #17).
import { t } from '../i18n/index.js';

const DEFAULT_TITLE = 'App Radar';
const DEFAULT_DESCRIPTION = () => t('tagline');
const DEFAULT_URL = () => `${window.location.origin}${window.location.pathname}`;

/**
 * Return or create a <meta> element with the given attribute and value.
 * @param {'name'|'property'} attr
 * @param {string} key
 * @returns {HTMLMetaElement}
 */
function getMeta(attr, key) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  return el;
}

/**
 * Set (or update) all OG / Twitter meta tags for a resolved app.
 *
 * @param {{ trackName: string, sellerName?: string, artworkUrl100?: string, artworkUrl60?: string, trackId: number|string }} app
 */
export function updateMetaTags(app) {
  const title = `${app.trackName} — App Radar`;
  const description = `Compare "${app.trackName}" by ${app.sellerName ?? 'unknown developer'} across Apple storefronts — ratings, prices, and availability worldwide.`;
  const image = app.artworkUrl512 ?? app.artworkUrl100 ?? app.artworkUrl60 ?? '';
  const url = (() => {
    const u = new URL(window.location.href);
    u.searchParams.set('appId', String(app.trackId));
    return u.toString();
  })();

  document.title = title;

  getMeta('property', 'og:title').setAttribute('content', title);
  getMeta('property', 'og:description').setAttribute('content', description);
  getMeta('property', 'og:url').setAttribute('content', url);
  getMeta('property', 'og:type').setAttribute('content', 'website');
  if (image) getMeta('property', 'og:image').setAttribute('content', image);

  getMeta('name', 'twitter:card').setAttribute('content', 'summary');
  getMeta('name', 'twitter:title').setAttribute('content', title);
  getMeta('name', 'twitter:description').setAttribute('content', description);
  if (image) getMeta('name', 'twitter:image').setAttribute('content', image);
}

export function updatePageMetaTags({ title, description, url = DEFAULT_URL(), image = '', card = 'summary' } = {}) {
  const pageTitle = String(title ?? '').trim() || DEFAULT_TITLE;
  const pageDescription = String(description ?? '').trim() || DEFAULT_DESCRIPTION();
  const pageUrl = String(url ?? '').trim() || DEFAULT_URL();

  document.title = pageTitle;

  getMeta('property', 'og:title').setAttribute('content', pageTitle);
  getMeta('property', 'og:description').setAttribute('content', pageDescription);
  getMeta('property', 'og:url').setAttribute('content', pageUrl);
  getMeta('property', 'og:type').setAttribute('content', 'website');
  if (image) getMeta('property', 'og:image').setAttribute('content', image);

  getMeta('name', 'twitter:card').setAttribute('content', card);
  getMeta('name', 'twitter:title').setAttribute('content', pageTitle);
  getMeta('name', 'twitter:description').setAttribute('content', pageDescription);
  if (image) getMeta('name', 'twitter:image').setAttribute('content', image);
}

/**
 * Reset meta tags to site defaults (call when a comparison is cleared).
 */
export function resetMetaTags() {
  document.title = DEFAULT_TITLE;

  getMeta('property', 'og:title').setAttribute('content', DEFAULT_TITLE);
  getMeta('property', 'og:description').setAttribute('content', DEFAULT_DESCRIPTION());
  getMeta('property', 'og:url').setAttribute('content', DEFAULT_URL());
  getMeta('property', 'og:type').setAttribute('content', 'website');

  getMeta('name', 'twitter:card').setAttribute('content', 'summary');
  getMeta('name', 'twitter:title').setAttribute('content', DEFAULT_TITLE);
  getMeta('name', 'twitter:description').setAttribute('content', DEFAULT_DESCRIPTION());
}
